/**
 * PanelConversationBar — shared conversation switcher for embedded AI panels.
 *
 * Renders in ChatShell's headerSlot (48px height). Shows:
 * - Left: current conversation title (or "新会话" placeholder)
 * - Right: "新会话" button + "历史会话" button (opens Popover with list)
 *
 * Behaviors:
 * - History popover lists panel conversations (title + relative time).
 * - Click item → onSelect(id); deletes current → onNew() after confirmation.
 * - Disabled state disables both buttons (used during streaming).
 * - testids follow kebab-case convention.
 */

import { type ReactNode } from "react";
import { PlusOutlined, HistoryOutlined, DeleteOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { Popover, Popconfirm, Empty, Button, Tooltip, Typography } from "antd";
import { useConversation, useDeleteConversation, usePanelConversations } from "../../api/queries";
import { formatRelativeTime } from "../../utils/date";
import type { ConversationAnchorType, Conversation } from "../../types";
import styles from "../../styles/panel-conversation-bar.module.css";

const { Text } = Typography;

export interface PanelConversationBarProps {
  /** The panel type (e.g., "workbench_review", "draft_workbench"). */
  anchorType: ConversationAnchorType;
  /** The entity id this panel is bound to (documentId, draftId, projectId, etc.). */
  anchorId: string | undefined;
  /** Current conversation id (null = hero state). */
  currentConversationId: string | null;
  /** Callback when user selects a conversation from history. */
  onSelect: (id: string) => void;
  /** Callback when user clicks "新会话" or deletes the current conversation. */
  onNew: () => void;
  /** Disable buttons (e.g., during streaming). */
  disabled?: boolean;
  /** 2026-08-28 右栏头部整改：是否展示会话标题。锚定型面板（起草/审查）的
   * 会话集合天然只属于当前实体，标题只是页面标题的重复——这两处传 false；
   * 尽调/舆情等无重复来源的面板保持默认 true。"当前处于哪个会话"由历史
   * 弹层的当前项高亮承担。 */
  showTitle?: boolean;
  /** 2026-08-28 右栏头部整改：渲染形态。"bar"（默认）为独占一行的会话条；
   * "inline" 只渲染动作按钮簇（无条形容器、无标题），供调用方嵌入 tab 条
   * 右端，把两条 48px 头部并成一条。 */
  variant?: "bar" | "inline";
}

/**
 * Panel conversation bar — renders a compact header with title + actions.
 *
 * Layout (48px height, matches ChatShell.shellHeader):
 * - Left: current title (conversation.title or "新会话")
 * - Right: [新会话] button + [历史会话] button → Popover
 */
export function PanelConversationBar({
  anchorType,
  anchorId,
  currentConversationId,
  onSelect,
  onNew,
  disabled = false,
  showTitle = true,
  variant = "bar",
}: PanelConversationBarProps) {
  const deleteMutation = useDeleteConversation();

  // Fetch current conversation details for title display.
  const { data: currentConv } = useConversation(
    currentConversationId ?? "",
  );

  const currentTitle = currentConversationId
    ? currentConv?.title ?? "未命名会话"
    : "新会话";

  // Handle conversation selection from history.
  const handleSelect = (id: string) => {
    onSelect(id);
  };

  // Handle conversation deletion with confirmation.
  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering row selection.
    deleteMutation.mutate(id, {
      onSuccess: () => {
        // If we deleted the current conversation, reset to hero state.
        if (id === currentConversationId) {
          onNew();
        }
      },
    });
  };

  // History popover content (rendered only when anchorId is present).
  const historyContent = anchorId ? (
    <HistoryPopoverContent
      anchorType={anchorType}
      anchorId={anchorId}
      currentConversationId={currentConversationId}
      onSelect={handleSelect}
      onDelete={handleDelete}
    />
  ) : null;

  // 动作按钮簇（2026-08-28 右栏头部整改：文字按钮改纯图标 + Tooltip——纯图标
  // 的可发现性与禁用原因说明都靠 Tooltip，不能只留原生 title；无障碍名经
  // aria-label 保持「新会话/历史会话」不变）。流式禁用时 Tooltip 写明原因。
  const actions = (
    <div className={styles.actions}>
      <Tooltip title={disabled ? "生成中，暂不可开新会话" : "新会话"}>
        <Button
          data-testid="panel-conversation-bar-new-btn"
          className={styles.iconBtn}
          type="text"
          size="small"
          icon={<PlusOutlined />}
          onClick={onNew}
          disabled={disabled}
          aria-label="新会话"
        />
      </Tooltip>
      {historyContent && (
        <Popover
          data-testid="panel-conversation-bar-popover"
          content={historyContent}
          title="历史会话"
          trigger="click"
          placement="bottomRight"
          overlayClassName={styles.historyPopover}
        >
          <Tooltip title={disabled ? "生成中，暂不可切换会话" : "历史会话"}>
            <Button
              data-testid="panel-conversation-bar-history-btn"
              className={styles.iconBtn}
              type="text"
              size="small"
              icon={<HistoryOutlined />}
              disabled={disabled}
              aria-label="历史会话"
            />
          </Tooltip>
        </Popover>
      )}
    </div>
  );

  // inline 形态：只输出动作簇，供调用方嵌入 tab 条右端（外层 testid 保留，
  // 既有测试与语义定位不变）。
  if (variant === "inline") {
    return (
      <div className={styles.inlineActions} data-testid="panel-conversation-bar">
        {actions}
      </div>
    );
  }

  return (
    <div
      className={styles.bar}
      data-testid="panel-conversation-bar"
    >
      {/* Left: current conversation title（showTitle=false 时省去，动作簇右靠） */}
      {showTitle && (
        <Text
          data-testid="panel-conversation-bar-title"
          className={styles.title}
          ellipsis
        >
          {currentTitle}
        </Text>
      )}

      {/* Right: action buttons */}
      {actions}
    </div>
  );
}

/**
 * History popover content — lists panel conversations with relative times.
 */
interface HistoryPopoverContentProps {
  anchorType: ConversationAnchorType;
  anchorId: string | undefined;
  currentConversationId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string, e: React.MouseEvent) => void;
}

function HistoryPopoverContent({
  anchorType,
  anchorId,
  currentConversationId,
  onSelect,
  onDelete,
}: HistoryPopoverContentProps) {
  // Query panel conversations for the history list.
  const { data, isLoading } = usePanelConversations({
    anchorType,
    anchorId,
    pageSize: 20,
  });

  const conversations = data?.data ?? [];

  if (isLoading) {
    return <div className={styles.loading}>加载中...</div>;
  }

  if (conversations.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="暂无历史会话"
        className={styles.empty}
      />
    );
  }

  return (
    <div className={styles.historyList}>
      {conversations.map((conv: Conversation) => {
        const isActive = conv.id === currentConversationId;
        const relativeTime = conv.updatedAt
          ? formatRelativeTime(conv.updatedAt)
          : "";

        return (
          <div
            key={conv.id}
            data-testid={`panel-conversation-bar-item-${conv.id}`}
            className={
              isActive
                ? `${styles.historyItem} ${styles.historyItemActive}`
                : styles.historyItem
            }
            onClick={() => onSelect(conv.id)}
          >
            {/* Left: title + time */}
            <div className={styles.itemMain}>
              <div className={styles.itemTitle}>
                {conv.title || "未命名会话"}
              </div>
              {relativeTime && (
                <div className={styles.itemTime}>
                  <ClockCircleOutlined className={styles.timeIcon} />
                  {relativeTime}
                </div>
              )}
            </div>

            {/* Right: delete button */}
            <Popconfirm
              title="确认删除"
              description="删除后无法恢复"
              onConfirm={(e) => {
                e?.stopPropagation();
                onDelete(conv.id, e as any);
              }}
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                data-testid={`panel-conversation-bar-item-delete-${conv.id}`}
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                danger
                className={styles.itemDeleteBtn}
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </div>
        );
      })}
    </div>
  );
}
